// Group photo feed: photos and shared catches as big cards (like Instagram), with likes and comments.
// Posts are the group's photo and catch messages, so anything shared in the chat appears here too.
// Likes ("like", sent quietly: no notification) and comments are encrypted group messages as well.
import type { Group, GroupMessage, Payload } from "../../shared/model.ts";
import { $, el, icon, toast } from "../dom.ts";
import { fmtNum, fmtWhen, t } from "../i18n.ts";
import { blobToDataUrl, shrink } from "../photos.ts";
import { cancelButton, closeSheet, grow, openSheet, primaryButton } from "../sheet.ts";
import { errorMessage } from "../util.ts";
import { openGroupSeason } from "../season.ts";
import { hasPlus } from "../billing.ts";
import { chatDialog, chatGroup, renderChat, spotButtons } from "./chat.ts";
import { groupMessages, me, send } from "./store.ts";
import { renderGroups } from "./ui.ts";

export type Post = Extract<GroupMessage, { type: "photo" | "catch" }>;
export const isPost = (m: GroupMessage): m is Post => m.type === "photo" || m.type === "catch";
type Comment = Extract<GroupMessage, { type: "comment" }>;

export interface Social {
  likers: (postId: string) => { who: string; name: string }[];
  comments: (postId: string) => Comment[];
}
/** Likes (each person's last tap wins) and comments for every post. */
export function foldSocial(msgs: GroupMessage[]): Social {
  const likes = new Map<string, Map<string, { on: boolean; name: string }>>(), comments = new Map<string, Comment[]>();
  for (const m of msgs) {
    if (m.type === "like" && m.postId) {
      if (!likes.has(m.postId)) likes.set(m.postId, new Map());
      likes.get(m.postId)!.set(m.from, { on: !!m.on, name: m.name });
    } else if (m.type === "comment" && m.postId && m.text) {
      if (!comments.has(m.postId)) comments.set(m.postId, []);
      comments.get(m.postId)!.push(m);
    }
  }
  return {
    likers: (id) => [...(likes.get(id) ?? new Map<string, { on: boolean; name: string }>())].filter(([, v]) => v.on).map(([who, v]) => ({ who, name: v.name })),
    comments: (id) => comments.get(id) ?? [],
  };
}

/* ---------- Chat / Feed tabs in the group window ---------- */
export let chatTab: "chat" | "feed" = "chat";
let openPostId: string | null = null;
export function setChatTab(tab: "chat" | "feed"): void {
  chatTab = tab;
  $("#ctab-chat").setAttribute("aria-selected", String(tab === "chat"));
  $("#ctab-feed").setAttribute("aria-selected", String(tab === "feed"));
  $("#chat-list").hidden = tab !== "chat";
  $("#chat-feed").hidden = tab !== "feed";
  $("#chat-form").hidden = tab !== "chat";
  $(".chat-tools").hidden = tab !== "chat";
  if (tab === "feed") void renderFeed(); else if (chatDialog.open) void renderChat(true);
}
$("#ctab-chat").addEventListener("click", () => setChatTab("chat"));
$("#ctab-feed").addEventListener("click", () => setChatTab("feed"));

/* ---------- cards ---------- */
function postMedia(m: Post): HTMLElement | null {
  if (m.type === "photo") return m.image ? el("img", { class: "fimg", src: m.image, alt: t("msgPhoto"), loading: "lazy" }) : null;
  return m.image ? el("img", { class: "fimg", src: m.image, alt: m.catch.species || "", loading: "lazy" }) : el("div", { class: "fnoimg" }, icon(m.catch.kind === "game" ? "game" : "fish"));
}
function postText(m: Post): HTMLElement | null {
  if (m.type === "photo") return m.text ? el("p", { class: "fcap", text: m.text }) : null;
  const c = m.catch;
  const size = [c.weightKg != null ? fmtNum(c.weightKg, 2) + " kg" : "", c.lengthCm != null ? fmtNum(c.lengthCm) + " cm" : "", c.released ? t("released") : ""].filter(Boolean).join(" · ");
  const wx = [c.temp != null ? Math.round(c.temp) + "°C" : "", c.wind != null ? fmtNum(c.wind, 0) + " m/s" : "", c.water != null ? t("waterChip", fmtNum(c.water, 0)) : ""].filter(Boolean).join(" · ");
  return el("div", { class: "fcatch" }, el("b", { text: c.species || t(c.kind || "fish") }), size ? el("span", { class: "num", text: size }) : null,
    el("small", { class: "num", text: [c.time ? fmtWhen(c.time) : "", c.place].filter(Boolean).join(" · ") }), wx ? el("small", { class: "num", text: wx }) : null);
}
function likeButton(g: Group, m: Post, social: Social): HTMLElement {
  const likers = social.likers(m.id), mine = likers.some((l) => l.who === me.id);
  return el("button", { class: "factbtn" + (mine ? " on" : ""), type: "button", "aria-pressed": String(mine), "aria-label": t(mine ? "feedUnlike" : "feedLike"),
    onclick: async (e) => { e.stopPropagation(); await feedSend(g, { type: "like", postId: m.id, on: !mine }, true); } },
    icon(mine ? "heartfull" : "heart"), el("span", { class: "num", text: likers.length ? String(likers.length) : "" }));
}
const avatar = (name: string) => el("span", { class: "gavatar small", "aria-hidden": "true", text: (name || "?").charAt(0).toUpperCase() });
const postHead = (m: Post) => el("div", { class: "fhead" }, avatar(m.name), el("div", {}, el("b", { text: m.name }), el("small", { class: "num", text: fmtWhen(m.ts) })));

export async function renderFeed(): Promise<void> {
  const g = chatGroup();
  if (!g || chatTab !== "feed") return;
  const msgs = await groupMessages(g.id), social = foldSocial(msgs);
  const posts = msgs.filter(isPost).reverse();
  $(".num", $("#ctab-feed")).textContent = posts.length ? String(posts.length) : "";
  const box = $("#chat-feed"), top = box.scrollTop;
  const add = el("div", { class: "frow" },
    el("button", { class: "btn primary fnew", type: "button", onclick: () => $("#feed-photo").click() }, icon("camera"), t("feedNew")),
    posts.length >= 3 ? el("button", { class: "btn fnew", type: "button", "data-plus": hasPlus() ? "" : "1", onclick: openGroupSeason }, icon("play"), t("seasonBtn")) : null);
  if (!posts.length) { box.replaceChildren(add, el("div", { class: "empty" }, icon("camera"), el("p", { text: t("feedEmpty") }))); return; }
  box.replaceChildren(add, ...posts.map((m) => {
    const cs = social.comments(m.id);
    return el("article", { class: "fcard" },
      postHead(m),
      el("button", { class: "fopen", type: "button", "aria-label": t("feedOpen"), onclick: () => void openPost(m.id) }, postMedia(m)),
      el("div", { class: "fbody" },
        el("div", { class: "factions" }, likeButton(g, m, social),
          el("button", { class: "factbtn", type: "button", "aria-label": t("feedComment"), onclick: () => void openPost(m.id, true) }, icon("comment"), el("span", { class: "num", text: cs.length ? String(cs.length) : "" }))),
        postText(m),
        ...cs.slice(-2).map((c) => el("p", { class: "fcomment" }, el("b", { text: c.name + " " }), c.text)),
        cs.length > 2 ? el("button", { class: "linkbtn", type: "button", onclick: () => void openPost(m.id) }, t("feedAllComments", cs.length)) : null));
  }));
  box.scrollTop = top;
}

/* ---------- one post: picture, likes, all comments ---------- */
const postDialog = $<HTMLDialogElement>("#post");
async function openPost(id: string, focusComment = false): Promise<void> {
  openPostId = id;
  if (!postDialog.open) postDialog.showModal();
  await renderPost();
  $<HTMLTextAreaElement>("#post-text").value = "";
  if (focusComment) setTimeout(() => $("#post-text").focus(), 50);
}
async function renderPost(): Promise<void> {
  const g = chatGroup();
  if (!g || !openPostId) return;
  const msgs = await groupMessages(g.id), m = msgs.find((x): x is Post => x.id === openPostId && isPost(x));
  if (!m) { postDialog.close(); return; }
  const social = foldSocial(msgs), likers = social.likers(m.id), cs = social.comments(m.id);
  $("#post-title").textContent = m.type === "catch" ? (m.catch.species || t("msgCatch", "")) : t("msgPhoto");
  $("#post-body").replaceChildren(...[postHead(m), postMedia(m),
    el("div", { class: "fbody" },
      el("div", { class: "factions" }, likeButton(g, m, social),
        likers.length ? el("small", { class: "flikers", text: t("feedLikedBy", likers.map((l) => (l.who === me.id ? t("msgYou") : l.name)).join(", ")) }) : null),
      postText(m),
      m.type === "catch" && m.catch.loc ? spotButtons(m.catch.loc.lat, m.catch.loc.lon, m.catch.species || t("fish"), m.catch.kind === "game" ? "stand" : "spot") : null,
      el("h3", { class: "subhead", text: cs.length ? t("feedComments", cs.length) : t("feedNoComments") }),
      ...cs.map((c) => el("div", { class: "pcomment" }, avatar(c.name),
        el("div", {}, el("p", {}, el("b", { text: c.name + " " }), c.text), el("small", { class: "num", text: fmtWhen(c.ts) })))))].filter((n): n is HTMLElement => n != null));
}
/** Redraws the open post (after new messages arrive). */
export async function refreshPost(): Promise<void> {
  if (postDialog.open) await renderPost();
}
$("#post-close").addEventListener("click", () => postDialog.close());
postDialog.addEventListener("close", () => { openPostId = null; });
$("#post-form").addEventListener("submit", async (e) => {
  e.preventDefault();
  const field = $<HTMLTextAreaElement>("#post-text"), text = field.value.trim(), g = chatGroup();
  if (!text || !g || !openPostId) return;
  if (await feedSend(g, { type: "comment", postId: openPostId, text })) field.value = "";
});

async function feedSend(g: Group, payload: Payload, quiet = false): Promise<boolean> {
  try { await send(g, payload, { quiet }); await refreshFeedViews(); renderGroups(); return true; }
  catch (err) { console.error(err); toast(errorMessage(err).includes("too big") ? t("msgTooBig") : t("msgNotSent")); return false; }
}
async function refreshFeedViews(): Promise<void> {
  if (chatTab === "feed") await renderFeed();
  await refreshPost();
  if (chatTab === "chat" && chatDialog.open) await renderChat(false);
}

// New photo post from the feed: pick a photo, add a caption, send. It also shows in the chat.
$("#feed-photo").addEventListener("change", async (e) => {
  const fileInput = e.currentTarget as HTMLInputElement, f = fileInput.files?.[0];
  fileInput.value = "";
  const g = chatGroup();
  if (!f || !g) return;
  let image: string;
  try { image = await blobToDataUrl(await shrink(f, 1280, 0.78)); } catch { toast(t("photoBad")); return; }
  openSheet(t("feedNew"), [el("img", { class: "fpreview", src: image, alt: "" }),
    el("label", { class: "f" }, el("span", { class: "lbl", text: t("feedCaption") }), el("textarea", { id: "feed-caption", rows: "3", placeholder: t("feedCaptionPh") }))],
    [grow(), cancelButton(), primaryButton(t("shareSend"), async (btn) => {
      btn.disabled = true;
      if (await feedSend(g, { type: "photo", image, text: $<HTMLTextAreaElement>("#feed-caption").value.trim() })) closeSheet();
      btn.disabled = false;
    })]);
});

/** The small "♥ 2 · 💬 3" line under a photo or catch in the chat; tapping it opens the post. */
export function socialFooter(m: GroupMessage, social: Social): HTMLElement | null {
  if (!isPost(m)) return null;
  const likers = social.likers(m.id), c = social.comments(m.id).length;
  return el("button", { class: "bsocial", type: "button", onclick: () => void openPost(m.id, !c) },
    icon(likers.some((x) => x.who === me.id) ? "heartfull" : "heart"), el("span", { class: "num", text: String(likers.length) }),
    icon("comment"), el("span", { class: "num", text: c ? String(c) : t("feedComment") }));
}
